import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'gatsby';

const isInternal = (href: string) => href.startsWith('/');

export const SmartLink: React.FC<{
  href: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}> = ({ href, className, children, onClick }) => {
  if (isInternal(href)) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  const external = href.startsWith('http');
  return (
    <a
      href={href}
      className={className}
      onClick={onClick}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
};

export const Corners = () => (
  <>
    <i className="ck ck--tl" />
    <i className="ck ck--tr" />
    <i className="ck ck--bl" />
    <i className="ck ck--br" />
  </>
);

export const Btn: React.FC<{
  href: string;
  variant?: 'primary' | 'ghost';
  small?: boolean;
  arrow?: boolean;
  children: React.ReactNode;
}> = ({ href, variant = 'primary', small, arrow, children }) => (
  <SmartLink href={href} className={`btn btn--${variant}${small ? ' btn--sm' : ''}`}>
    <Corners />
    {children}
    {arrow && <span className="arr">→</span>}
  </SmartLink>
);

export const Panel: React.FC<{
  className?: string;
  dots?: boolean;
  children: React.ReactNode;
}> = ({ className = '', dots, children }) => (
  <div className={`panel${dots ? ' panel--dots' : ''} ${className}`.trim()}>
    <div className="panel__in">{children}</div>
  </div>
);

export const SecHead: React.FC<{ title: string; chip: string; sub?: string; heading?: 'h1' | 'h2' }> = ({
  title,
  chip,
  sub,
  heading = 'h2',
}) => {
  const Tag = heading;
  return (
  <>
    <div className="sec-head rv">
      <Tag className="sec-title">
        <span className="sl">//</span>
        {title}
      </Tag>
      <span className="chip chip--accent">{chip}</span>
    </div>
    {sub && <p className="sec-sub rv">{sub}</p>}
  </>
  );
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useInView = <T extends Element>(threshold = 0.3) => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, inView };
};

export const CountUp: React.FC<{
  to: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  className?: string;
}> = ({ to, prefix = '', suffix = '', duration = 1400, className }) => {
  const { ref, inView } = useInView<HTMLSpanElement>(0.4);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (prefersReducedMotion()) {
      setValue(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {value.toLocaleString('en-US')}
      {suffix}
    </span>
  );
};

export { prefersReducedMotion };
